import type { RefObject } from 'react';
import { useCallback, useRef } from 'react';

/**
 * Jump-link scrolling and skip-link focus for a content container.
 *
 * Accepts an external `ref` for callers whose content element is rendered by
 * a different component than the one calling the hook (see DetailSectionNav);
 * otherwise the hook owns the ref.
 */
export function useScrollToContent(ref?: RefObject<HTMLDivElement | null>) {
  const ownRef = useRef<HTMLDivElement>(null);
  const contentRef = ref ?? ownRef;

  // Returns whether a section was found, so callers can skip side effects
  // (e.g. locking the spy) when the target isn't rendered.
  const scrollTo = useCallback(
    (sectionId: string): boolean => {
      const section = contentRef.current?.querySelector<HTMLElement>(
        `#${sectionId}`,
      );

      if (!section) {
        return false;
      }

      section.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Move focus with the jump so the next Tab continues inside the target, not
      // the nav; preventScroll keeps focus from cutting the smooth scroll short.
      section.tabIndex = -1;
      section.focus({ preventScroll: true });

      return true;
    },
    [contentRef],
  );

  // Skip-link: land focus at the top of content without moving the viewport.
  const skipToContent = useCallback(() => {
    const content = contentRef.current;

    if (!content) {
      return;
    }

    content.tabIndex = -1;
    content.focus({ preventScroll: true });
  }, [contentRef]);

  return {
    contentRef,
    scrollTo,
    skipToContent,
  };
}
